import React from 'react';
import {
  Check, X, Anchor, Smile, BookOpen, Zap, Eye, HelpCircle, Quote, Award,
} from 'lucide-react';
import { useT } from '@/lib/i18n';

const ICON_BY_KEY = {
  hook: Anchor, emotion: Smile, storytelling: BookOpen, energy: Zap,
  curiosity: Eye, question: HelpCircle, quote: Quote, ending: Award,
};

const ReasonBadge = ({ reason, lang }) => {
  const Icon = ICON_BY_KEY[reason.key] || Check;
  const label = lang === 'fr' ? reason.label_fr : reason.label_en;
  const met = !!reason.met;
  return (
    <div
      className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
        met ? 'border-[#D4FF00]/30 bg-[#D4FF00]/5 text-zinc-200'
            : 'border-white/5 bg-white/[0.02] text-zinc-600'
      }`}
      data-testid={`reason-${reason.key}`}
    >
      <div className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 ${
        met ? 'bg-[#D4FF00]/15 text-[#D4FF00]' : 'bg-white/5 text-zinc-700'
      }`}>
        {met ? <Check className="w-3.5 h-3.5" strokeWidth={3} /> : <X className="w-3.5 h-3.5" />}
      </div>
      <Icon className={`w-3.5 h-3.5 shrink-0 ${met ? 'text-zinc-400' : 'text-zinc-700'}`} />
      <span className="text-xs sm:text-sm">{label}</span>
    </div>
  );
};

/** "Why this clip" — card with reason checkmarks. */
const WhyThisClipCard = ({ insights, loading }) => {
  const { lang } = useT();
  if (loading) {
    return (
      <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-6">
        <div className="h-5 w-48 bg-white/5 rounded animate-pulse mb-4" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-10 bg-white/5 rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }
  const reasons = insights?.reasons || [];
  const metCount = reasons.filter((r) => r.met).length;
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-6" data-testid="why-this-clip-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-bold text-base sm:text-lg">
          {lang === 'fr' ? 'Pourquoi l\'IA a sélectionné ce clip' : 'Why the AI selected this clip'}
        </h3>
        <span className="text-xs font-mono text-zinc-500">{metCount}/{reasons.length}</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {reasons.map((r) => <ReasonBadge key={r.key} reason={r} lang={lang} />)}
      </div>
    </div>
  );
};

export default WhyThisClipCard;
